import SearchAPI from 'dashboard/api/search';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/conversations';
import EventsAPI from 'dashboard/api/planning/events';
import { formatEventOccurrenceDate } from 'shared/helpers/DateHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  ROLES,
  CONTACT_PERMISSIONS,
  CONVERSATION_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions';

export const MENTION_CATEGORIES = [
  {
    id: 'contacts',
    labelKey: 'SEARCH.TABS.CONTACTS',
    icon: 'i-lucide-user',
    permissions: [...ROLES, ...CONTACT_PERMISSIONS],
  },
  {
    id: 'conversations',
    labelKey: 'SEARCH.TABS.CONVERSATIONS',
    icon: 'i-lucide-message-circle',
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
  },
  {
    id: 'articles',
    labelKey: 'SEARCH.TABS.ARTICLES',
    icon: 'i-lucide-book-open',
    permissions: [...ROLES, PORTAL_PERMISSIONS],
    featureFlag: FEATURE_FLAGS.HELP_CENTER,
  },
  {
    id: 'events',
    labelKey: 'SEARCH.TABS.EVENTS',
    icon: 'i-lucide-calendar-days',
    permissions: ROLES,
    featureFlag: FEATURE_FLAGS.PLANNING,
  },
];

export function normalizeContact(contact) {
  return {
    type: 'contact',
    id: contact.id,
    name: contact.name || contact.email || contact.phone_number || 'Contact',
    subtitle: contact.email || contact.phone_number || '',
    raw: contact,
  };
}

export function normalizeConversation(conversation) {
  const contactName = conversation.contact?.name;
  const subject = conversation.additional_attributes?.mail_subject;
  return {
    type: 'conversation',
    id: conversation.id,
    name: contactName || `Conversation #${conversation.id}`,
    subtitle: subject || conversation.inbox?.name || '',
    raw: conversation,
  };
}

export function normalizeArticle(article) {
  return {
    type: 'article',
    id: article.id,
    name: article.title || 'Article',
    subtitle: article.category_name || article.status || '',
    portalSlug: article.portal_slug,
    raw: article,
  };
}

export function normalizeEvent(event) {
  return {
    type: 'event',
    id: event.id,
    name: event.name || 'Untitled',
    subtitle: event.custom_status || event.system_stage || '',
    date: formatEventOccurrenceDate(event),
    raw: event,
  };
}

const SEARCH_HANDLERS = {
  contacts: async q => {
    const { data } = await SearchAPI.contacts({ q, page: 1 });
    return (data.payload?.contacts || []).map(normalizeContact);
  },
  conversations: async q => {
    const { data } = await SearchAPI.conversations({ q, page: 1 });
    return (data.payload?.conversations || []).map(normalizeConversation);
  },
  articles: async q => {
    const { data } = await SearchAPI.articles({ q, page: 1 });
    return (data.payload?.articles || []).map(normalizeArticle);
  },
  events: async q => {
    const { data } = await SearchAPI.events({ q, page: 1 });
    return (data.payload?.events || []).map(normalizeEvent);
  },
};

const RECENT_HANDLERS = {
  contacts: async () => {
    const { data } = await ContactAPI.get(1, 'last_activity_at');
    return (data.payload || []).map(normalizeContact);
  },
  conversations: async () => {
    const { data } = await ConversationApi.getRecentConversations(15);
    const records = data.data?.payload || data.payload || [];
    return records.map(normalizeConversation);
  },
  articles: async () => [],
  events: async () => {
    const response = await EventsAPI.getAll({ per_page: 15, page: 1 });
    return (response.data?.data || []).map(normalizeEvent);
  },
};

export async function searchMentionCategory(categoryId, query) {
  const handler = SEARCH_HANDLERS[categoryId];
  if (!handler) return [];
  return handler(query.trim());
}

export async function loadRecentMentionCategory(categoryId) {
  const handler = RECENT_HANDLERS[categoryId];
  if (!handler) return [];
  return handler();
}

export async function searchAllMentionCategories(categoryIds, query) {
  const results = await Promise.all(
    categoryIds.map(async categoryId => {
      try {
        const items = await searchMentionCategory(categoryId, query);
        return items.slice(0, 5);
      } catch {
        return [];
      }
    })
  );
  return results.flat();
}

export function mentionItemKey(item) {
  return `${item.type}:${item.id}`;
}
